import React from "react";
import { createRoot } from "react-dom/client";
import "./index.css";
import App from "@/App";
import { UserProvider } from "@/context/UserContext";
import { LiveVariantProvider } from "@/context/LiveVariantContext";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import axios from "axios";
import moment from "moment";
import config from "@/config";
import { JSX } from "react";
import { logger } from "@/services/LoggerService";
import ErrorBoundary from "@/components/common/ErrorBoundary/ErrorBoundary";

const rootElement = document.getElementById("root");
if (!rootElement) throw new Error("Failed to find the root element");

// Add a custom timezone header to all axios requests
axios.defaults.headers.common["Client-Timezone"] = moment.tz.guess(true);

const fiveMinutesInMs = 1000 * 60 * 5;
const queryClient = new QueryClient({
    defaultOptions: {
        queries: {
            refetchOnWindowFocus: false,
            refetchOnMount: false,
            refetchOnReconnect: false,
            retry: false,
            staleTime: fiveMinutesInMs, // Reduced from 24 hours to 5 minutes
        },
    },
});

// Initialize Logtail logger
logger.initialize();

// Set up global error handlers for runtime errors
// This handler catches both JavaScript errors and resource loading errors
window.addEventListener(
    "error",
    (event) => {
        // Check if it's a resource loading error (image, script, stylesheet, etc.)
        if (event.target && event.target !== window && event.target !== document) {
            const target = event.target as HTMLElement;
            logger.error("Resource loading error", new Error(`Failed to load resource: ${target.tagName}`), {
                errorType: "resource",
                resourceType: target.tagName,
                resourceSrc:
                    (target as HTMLImageElement).src ||
                    (target as HTMLLinkElement).href ||
                    (target as HTMLScriptElement).src ||
                    "unknown",
                globalError: true,
            });
        } else {
            // This is a JavaScript runtime error
            const error = event.error || new Error(event.message);
            logger.error("JavaScript runtime error", error, {
                filename: event.filename,
                lineno: event.lineno,
                colno: event.colno,
                errorType: "runtime",
                globalError: true,
            });
        }
    },
    true,
); // Use capture phase to catch errors from all elements

// Capture unhandled promise rejections (async errors)
window.addEventListener("unhandledrejection", (event) => {
    const reason = event.reason;
    const error = reason instanceof Error ? reason : new Error(String(reason));
    logger.error("Unhandled promise rejection", error, {
        errorType: "unhandledRejection",
        globalError: true,
        promiseRejection: true,
    });
});

// Flush logs before page unload
// Use visibilitychange for better reliability (works when tab is hidden/closed)
document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "hidden") {
        // Use sendBeacon for more reliable log flushing on page unload
        logger.flush();
    }
});

// Fallback: also flush on beforeunload
window.addEventListener("beforeunload", () => {
    logger.flush();
});

// Expose only the Mixpanel token for Mixpanel initialization
declare global {
    interface Window {
        MIXPANEL_TOKEN: string | null;
    }
}
window.MIXPANEL_TOKEN = config.MIXPANEL_TOKEN;

const AppWrapper: JSX.Element = (
    <React.StrictMode>
        <ErrorBoundary>
            <LiveVariantProvider>
                <UserProvider>
                    <QueryClientProvider client={queryClient}>
                        <App />
                    </QueryClientProvider>
                </UserProvider>
            </LiveVariantProvider>
        </ErrorBoundary>
    </React.StrictMode>
);

/**
 * Renders the application with proper hydration support for react-snap
 */
// if (rootElement.hasChildNodes()) {
//     try {
//         hydrateRoot(rootElement, AppWrapper);
//     } catch (error) {
//         console.error("Hydration failed, falling back to client-side rendering:", error);
//         const hydrationError = error instanceof Error ? error : new Error(String(error));
//         logger.error("Hydration failed, falling back to client-side rendering", hydrationError, {
//             hydrationError: true,
//         });
//         createRoot(rootElement).render(AppWrapper);
//     }
// } else {
createRoot(rootElement).render(AppWrapper);
// }

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
// This is commented out for now to avoid unnecessary console logs during normal operation
// reportWebVitals(console.log);
